import { FC } from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from '@packages/utils';
import ButtonGroup from 'components/Base/ButtonGroup/ButtonGroup';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { EWorkshopLanguage, TRtkErrorType } from 'data/types/generic.types';
import { EWorkshopStatus } from 'data/types/workshops.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { useUpdateRegistrantMaterialsLanguageMutation } from 'redux/apiSlice/registrants.slice';
import { TKitLanguageToggleProps } from './KitLanguageToggle.types';

// GROW-4365: kit-language toggle for workshop registrants. Enabled only while
// the registrant has a received kit; the BE moves the inventory deduction to
// the new language. Allowed after CLOSED, rejected when CANCELLED.
const KitLanguageToggle: FC<TKitLanguageToggleProps> = ({
  workshopId,
  registrantId,
  materialsLanguage,
  hasReceivedKit,
  workshopStatus,
}) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.workshop-registrants.kit-language',
  });
  const { showToastNotification } = useShowToastNotification();
  const canEdit = useCheckPermission(
    'WORKSHOP_REGISTRANTS_EDIT_SESSION_ATTENDANCE',
  );
  const [updateMaterialsLanguage, { isLoading }] =
    useUpdateRegistrantMaterialsLanguageMutation();

  const isDisabled =
    !hasReceivedKit ||
    !canEdit ||
    workshopStatus === EWorkshopStatus.CANCELLED ||
    isLoading;

  const handleChange = async (value: string) => {
    if (value === materialsLanguage) return;
    try {
      await updateMaterialsLanguage({
        workshopId,
        registrantId,
        materialsLanguage: value as EWorkshopLanguage,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message || t('ERROR_MESSAGE'),
        type: ENotificationType.ERROR,
      });
    }
  };

  const toggle = (
    <ButtonGroup
      options={[
        {
          label: t('ENGLISH'),
          value: EWorkshopLanguage.ENGLISH,
          dataCy: 'kit-language-english',
        },
        {
          label: t('SPANISH'),
          value: EWorkshopLanguage.SPANISH,
          dataCy: 'kit-language-spanish',
        },
      ]}
      value={materialsLanguage}
      size="small"
      disabled={isDisabled}
      onChange={handleChange}
    />
  );

  if (!hasReceivedKit) {
    return (
      <Tooltip title={t('DISABLED_TOOLTIP')}>
        <span style={{ display: 'inline-flex' }}>{toggle}</span>
      </Tooltip>
    );
  }

  return toggle;
};

export default KitLanguageToggle;
